import Big from 'big.js';
import axios from 'smg/axios';
import * as carts from 'smg/customer-api/carts';
import Modal from 'smg/modal';
import { Clone } from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import { Relref } from './hugo';

export const GetCart = carts.GetCart;
export const GetCartID = carts.GetCartID;
export const GetCartCount = carts.GetCartCount;
export const DeleteCart = carts.DeleteCart;
let cartPrompted: boolean = false;
let cartID: string | null = null;

export declare interface AddCartItemOpts {
    promptToCheckout?: boolean;
    hideCartLink?: boolean;
    showToast?: boolean;
    newCart?: boolean;
}

const defaultAddCartItemOpts = {
    promptToCheckout: true,
    hideCartLink: false,
    showToast: true,
    newCart: false,
};

export async function AddCartItem(cartItems: carts.AddCartItemParams | carts.AddCartItemParams[], opts: AddCartItemOpts = defaultAddCartItemOpts) {
    const promptToCheckout = opts.promptToCheckout ?? defaultAddCartItemOpts.promptToCheckout;
    const hideCartLink = opts.hideCartLink ?? defaultAddCartItemOpts.hideCartLink;
    const showToast = opts.showToast ?? defaultAddCartItemOpts.showToast;
    const newCart = opts.newCart ?? defaultAddCartItemOpts.newCart;

    return carts.AddCartItems(cartItems, newCart).then(resp => {
        cartID = resp.cartID;
        carts.SetCartID(cartID);

        if (showToast) {
            toast.Success(`Added to cart! ${hideCartLink ? '' : `<a href='${Relref('/cart')}' class="text-white text-decoration-underline">Go to cart</a>`}`);
        }

        if (promptToCheckout && !cartPrompted) {
            new Modal({
                title: 'Quote added to your cart!',
                text: 'Ready to checkout?',
                confirmText: 'Go to cart',
                cancelText: 'Keep shopping',
                onSubmit: () => {
                    window.location.href = Relref('/cart');
                },
            });
            cartPrompted = true;
        }

        if (newCart) {
            ResetCartCount();
        }

        SetCartCount(resp.cartItemsCount);
    }).catch(err => console.error(err));
}

export async function DeleteCartItem(cartItemID: string, quantity: number = 1, onUndo?: () => void) {
    return axios.delete(`/cart-items/${cartItemID}`).
        then(() => {
            const undoLinkID = `undo-${UUID()}`;
            const t = toast.Success(`Removed from cart. <a href="${Relref('/cart')}" class="text-white text-decoration-underline" id="${undoLinkID}">Undo</a>`);

            const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
            undo.addEventListener('click', async e => {
                e.preventDefault();

                await carts.RestoreCartItem(cartItemID).
                    then(() => IncrementCartCount(quantity)).
                    then(() => onUndo && onUndo());

                t?.dispose();
            });
            DecrementCartCount(quantity);
        });
}

let cartCount: number = 0;
let navCartLink: HTMLElement | undefined;
let navCartCount: NodeListOf<HTMLElement> | undefined;
let navCartIcon: HTMLElement | undefined;

function initCarNavLinks() {
    if (!navCartLink || !navCartCount || !navCartIcon) {
        navCartLink = document.querySelector('#nav-cart-link') as HTMLElement;
        navCartCount = document.querySelectorAll('.nav-cart-count');
        navCartIcon = document.querySelector('#shopping-cart-icon') as HTMLElement;
    }
}

export function SetCartCount(qty: number) {
    initCarNavLinks();

    navCartIcon?.classList.remove('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            el.classList.remove('d-none');
            el.textContent = String(qty);
        });
    }
}

export function IncrementCartCount(add: number = 1) {
    initCarNavLinks();

    cartCount += add;

    navCartIcon?.classList.remove('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            el.classList.remove('d-none');
            el.textContent = String(cartCount);
        });
    }
}

export function DecrementCartCount(minus: number = 1) {
    initCarNavLinks();

    cartCount -= minus;
    cartCount = Math.max(cartCount, 0);

    navCartIcon?.classList.add('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            if (cartCount < 1) {
                el.classList.add('d-none');
                return;
            }
            el.classList.remove('d-none');
            el.textContent = String(cartCount);
        });
    }
}

export function ResetCartCount() {
    DecrementCartCount(cartCount);
}

export function GetCurrentCartCount() {
    return cartCount;
}

export async function PromptQuantityEdit(defaultQty: number, quantityPricesObj: Record<string, string>): Promise<number> {
    const body = Clone('#cart-item-quantity-editor-template').firstElementChild as HTMLElement;

    const quantityPrices = Object.keys(quantityPricesObj).map(q => ({ qty: parseInt(q, 10), price: quantityPricesObj[q] })).sort((a, b) => a.qty - b.qty);

    const optionsParent = body.querySelector<HTMLElement>('.qty-editor-options-parent');

    let selectedQty = false;

    // find the price from the quantity prices thats the last quantity before the given qty
    const getPriceFromQty = (qty: number) => {
        for (let i = quantityPrices.length - 1; i >= 0; i--) {
            if (quantityPrices[i].qty <= qty) {
                return quantityPrices[i].price;
            }
        }
    };

    const customQtyInputContainer = body.querySelector<HTMLElement>('.qty-editor-custom-input-container');
    const customQtyInput = body.querySelector<HTMLInputElement>('.qty-editor-custom-input');
    const customQtyTotal = customQtyInputContainer?.querySelector<HTMLElement>('.qty-editor-total');

    const drawCustomTotals = () => {
        if (customQtyInputContainer) {
            const qty = parseInt(customQtyInput?.value || '0', 10);
            const price = getPriceFromQty(qty);

            if (customQtyTotal) {
                customQtyTotal.innerText = DollarFormat(Big(price || 0).times(qty));
            }

            if (price) {
                drawSavings(customQtyInputContainer, qty, Big(price));
            } else {
                drawSavings(customQtyInputContainer);
            }
        }
    };

    const showCustom = (show: boolean) => {
        customQtyInputContainer?.classList.toggle('d-none', !show);
        if (customQtyInput) {
            customQtyInput.disabled = !show;
        }

        drawCustomTotals();

        setTimeout(() => {
            customQtyInput?.focus();
            customQtyInput?.select();
        });
    };

    if (customQtyInput) {
        customQtyInput.value = String(defaultQty);
    }

    const drawSavings = (el: HTMLElement, qty?: number, price?: Big) => {
        const saveContainer = el.querySelector<HTMLElement>('.qty-editor-save-container');
        const savePercent = saveContainer?.querySelector<HTMLElement>('.qty-editor-save-percent');

        if (!saveContainer || !savePercent) {
            return;
        }

        saveContainer.classList.add('d-none');

        if (qty && price) {
            const totalWithoutDiscount = Big(quantityPrices[0].price).times(qty);
            const total = price.times(qty);
            const savings = totalWithoutDiscount.minus(total);
            const save = savings.div(totalWithoutDiscount);
            savePercent.textContent = Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0 }).format(save.toNumber());

            if (saveContainer && save.gt(0)) {
                saveContainer.classList.remove('d-none');
            }
        }
    };

    if (quantityPrices.length <= 1) {
        optionsParent?.classList.add('d-none');
    } else {
        for (const qp of quantityPrices) {
            if (customQtyInput && !customQtyInput.min) {
                customQtyInput.min = String(qp.qty);
            }

            const el = Clone('.cart-item-quantity-editor-qty-template', body).firstElementChild as HTMLElement;
            const qty = el.querySelector<HTMLElement>('.qty-editor-qty');
            const price = el.querySelector<HTMLElement>('.qty-editor-price');
            const total = el.querySelector<HTMLElement>('.qty-editor-total');

            if (qty) {
                qty.textContent = Intl.NumberFormat().format(qp.qty);
            }

            if (price) {
                price.textContent = DollarFormat(qp.price);
            }

            if (total) {
                total.textContent = DollarFormat(qp.qty * parseFloat(qp.price));
            }

            drawSavings(el, qp.qty, Big(qp.price));

            const radio = el.querySelector('input') as HTMLInputElement;
            radio.value = String(qp.qty);

            if (defaultQty === qp.qty) {
                radio.checked = true;
                selectedQty = true;

                if (customQtyInput) {
                    customQtyInput.value = String(qp.qty);
                }

                showCustom(false);
            }

            radio.addEventListener('click', () => {
                showCustom(false);
                if (customQtyInput) {
                    customQtyInput.value = String(qp.qty);
                    customQtyInput.dispatchEvent(new Event('input'));
                }
            });

            optionsParent?.appendChild(el);
        }
    }

    const customOption = body.querySelector<HTMLElement>('.qty-editor-custom-option');
    if (customOption) {
        optionsParent?.appendChild(customOption);
        const radio = customOption?.querySelector('input') as HTMLInputElement;

        if (!selectedQty) {
            radio.checked = true;
            showCustom(true);
        }

        radio.addEventListener('click', () => {
            showCustom(true);
        });

        customQtyInput?.addEventListener('input', () => drawCustomTotals());
    }

    return new Promise((resolve, reject) => {
        new Modal({
            body,
            title: 'Edit Quantity',
            confirmText: 'Save',
            onSubmit: () => {
                const checked = body.querySelector<HTMLInputElement>('input:checked');
                if (checked?.value === 'custom') {
                    resolve(parseInt(customQtyInput?.value || '0', 10));
                } else {
                    resolve(parseInt(checked?.value || '0', 10));
                }
            },
            onClose: () => reject('Modal closed'),
            // size: 'modal-sm',
        });

        showCustom(customQtyInputContainer?.classList.contains('d-none') === false);
    });
}